import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
    EmploymentGridItemObject,
    EmploymentWorkplaceObject,
} from '@libs/types/src/user/object-type/employment-grid.object';
import { FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { WorkloadTypeEnum } from '@libs/types/src/user/enum/workload-type.enum';
import { ServiceGridItemObject } from '@libs/types/src/services/object-type/service.object';
import { WorkplaceObject } from '@libs/types/src/services/object-type/workplace.object';
import { contractVisibility } from '../shared/helpers';
import { UserEmploymentDialogStore } from './user-employment-dialog.store';
import { EmploymentContractEnum } from '@libs/types/src/user/enum/employment-contract.enum';
import {
    CreateEmploymentInput,
    EmploymentWorkplaceInput,
} from '@libs/types/src/user/input-type/create-employment.input';
import { isInvalid } from '../../../../shared/helpers/validate';
import { UpdateEmploymentInput } from '@libs/types/src/user/input-type/update-employment.input';
import { BaseDialogComponent } from '../../../../../components/kit/base-dialog/base-dialog.component';
import { fb } from '../../../../shared/shared.global-service';
import { contentGlobalSuccess } from '../../../content.error';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { getServicesFrag } from './user-employment-dialog.query';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import {
    DeleteButtonComponent,
} from '../../../../../components/kit/button/delete-button.component';
import {
    EmploymentTypeSelectInputComponent,
} from '../../../../../components/kit/input/employment-type-select-input/employment-type-select-input.component';
import { SelectInputComponent } from '../../../../../components/kit/input/select-input/select-input.component';
import { TextInputComponent } from '../../../../../components/kit/input/text-input/text-input.component';
import { CardModule } from '@openng/optimus-ui/card';
import { InputNumberModule } from '@openng/optimus-ui/inputnumber';
import { TranslatePipe } from '@ngx-translate/core';
import { UserEmploymentWorkplaceFormControls } from './user-employment-dialog.types';

@Component({
    selector: 'app-user-employment-dialog',
    templateUrl: './user-employment-dialog.component.html',
    styleUrl: './user-employment-dialog.component.scss',
    providers: [UserEmploymentDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        CardModule,
        DateInputComponent,
        DeleteButtonComponent,
        EmploymentTypeSelectInputComponent,
        InputNumberModule,
        ReactiveFormsModule,
        SelectInputComponent,
        TextInputComponent,
        TranslatePipe,
    ],
})
export class UserEmploymentDialogComponent extends BaseDialogComponent implements OnInit {

    public readonly store = inject(UserEmploymentDialogStore);

    employment: EmploymentGridItemObject | null = null;
    userId: string | null = null;

    protected readonly workplacesControl = fb.array<FormGroup<UserEmploymentWorkplaceFormControls>>([]);

    protected form = fb.group({
        dateFrom: fb.control<Date>(new Date(), [Validators.required]),
        dateTo: fb.control<Date | null>(null),
        service: fb.control<number | null>(null, [Validators.required]),
        contract: fb.control<string | null>(null, [Validators.required]),
        specificName: fb.control<string | null>(null),
        employmentTypeName: fb.control<string | null>(null, [Validators.required]),

        workloadType: fb.control<WorkloadTypeEnum | null>(null),
        workloadSize: fb.control<number | null>(null),
        scopeOfWork: fb.control<number | null>(null),
        workplaces: this.workplacesControl,
        note: fb.control<string | null>(null),
    });

    protected workloadVisible = false;
    protected scopeOfWorkVisible = false;

    protected workplaces: WorkplaceObject[] = [];
    protected services: ServiceGridItemObject[] = [];
    protected workloadOptions = Object.values(WorkloadTypeEnum).map((it) => ({ name: it }));
    protected contractOptions = Object.values(EmploymentContractEnum).map((it) => ({ name: it }));

    constructor() {
        super();
        this.subs.push(
            this.form.controls.contract.valueChanges.subscribe((it) => {
                const visibility = contractVisibility(it);
                this.workloadVisible = visibility.workloadVisible;
                this.scopeOfWorkVisible = visibility.scopeOfWorkVisible;
                this.form.patchValue({ workloadSize: null, scopeOfWork: null });
            }),
            this.form.controls.service.valueChanges.subscribe((it) => {
                this.setServiceWorkplaces(it);
            }),
        );
    }

    public async ngOnInit(): Promise<void> {
        this.employment = this.dialogConfig.data?.employment;
        this.userId = this.dialogConfig.data?.userId;
        this.services = (await this.store.api.getServices(FilterBuilder.singlePage({}), getServicesFrag))
            ?.['items'] || [];
        if (this.employment) {
            this.form.patchValue({
                dateFrom: this.employment.dateFrom ? new Date(this.employment.dateFrom) : null,
                dateTo: this.employment.dateTo ? new Date(this.employment.dateTo) : null,
                service: this.employment.serviceId,
                contract: this.employment.contract,
                specificName: this.employment.specificName,
                employmentTypeName: this.employment.employmentTypeName,
                workloadType: this.employment.workloadType as WorkloadTypeEnum,
                workloadSize: this.employment.workloadSize,
                scopeOfWork: this.employment.scopeOfWork,
                note: this.employment.note,
            });
            this.workplacesControl.clear();
            this.employment.workplaces?.forEach(workplace => this.addWorkplace(workplace));
        } else {
            this.setServiceWorkplaces(this.form.controls.service.value);
        }
    }

    public override async save(): Promise<void> {
        if (this.employment) {
            if (await this.updateEmployment()) {
                contentGlobalSuccess.next('common.updated');
                this.dialogRef.close(true);
            }
        } else {
            if (await this.saveEmployment()) {
                contentGlobalSuccess.next('common.created');
                this.dialogRef.close(true);
            }
        }
    }

    public async updateEmployment(): Promise<boolean> {
        if (isInvalid(this.form) || !this.userId) return false;
        const employment: UpdateEmploymentInput = {
            id: this.employment!.id,
            dateFrom: this.form.value.dateFrom!.toISOString(),
            dateTo: this.form.value.dateTo?.toISOString() || null,
            serviceId: this.form.value.service!,
            organizationId: this.employment!.organizationId,
            workplaces: this.getWorkplacesInput(),
            contract: this.form.value.contract! as EmploymentContractEnum,
            workloadSize: this.form.value.workloadSize ?? null,
            workloadType: this.form.value.workloadType ?? null,
            employmentTypeName: this.form.value.employmentTypeName!,
            userId: this.userId,
            scopeOfWork: this.form.value.scopeOfWork ?? null,
            specificName: this.form.value.specificName ?? null,
            note: this.form.value.note ?? null,
        };
        await this.store.api.updateEmployment(employment);
        return true;
    }

    public async saveEmployment(): Promise<boolean> {
        if (isInvalid(this.form) || !this.userId) return false;
        const employment: CreateEmploymentInput = {
            dateFrom: this.form.value.dateFrom!.toISOString(),
            dateTo: this.form.value.dateTo?.toISOString() || null,
            serviceId: this.form.value.service!,
            workplaces: this.getWorkplacesInput(),
            contract: this.form.value.contract! as EmploymentContractEnum,
            workloadSize: this.form.value.workloadSize ?? null,
            workloadType: this.form.value.workloadType ?? null,
            employmentTypeName: this.form.value.employmentTypeName!,
            userId: this.userId,
            scopeOfWork: this.form.value.scopeOfWork ?? null,
            specificName: this.form.value.specificName ?? null,
            note: this.form.value.note ?? null,
        };

        await this.store.api.createEmployment(employment);
        return true;
    }

    protected addWorkplace(employment?: EmploymentWorkplaceObject): void {
        const employmentWorkplaceExists = this.workplaces.some(
            workplace => workplace.id === employment?.workplaceId,
        );
        const workplaceId = employmentWorkplaceExists
            ? employment!.workplaceId
            : this.workplaces.length === 1
                ? this.workplaces[0].id
                : null;

        this.workplacesControl.push(fb.group<UserEmploymentWorkplaceFormControls>({
            workplaceId: fb.control<number | null>(workplaceId),
            w_dateFrom: fb.control<Date | null>(
                employment?.dateFrom ? new Date(employment.dateFrom) : new Date(),
            ),
            w_dateTo: fb.control<Date | null>(employment?.dateTo ? new Date(employment.dateTo) : null),
        }));
    }

    protected deleteWorkplace(index: number): void {
        this.workplacesControl.removeAt(index);
    }

    private setServiceWorkplaces(serviceId: number | null): void {
        this.workplaces = this.services.find(service => service.id === serviceId)?.workplaces ?? [];
        const defaultWorkplaceId = this.workplaces.length === 1 ? this.workplaces[0].id : null;

        this.workplacesControl.controls.forEach(control => {
            const workplaceId = control.controls.workplaceId.value;
            const workplaceExists = this.workplaces.some(workplace => workplace.id === workplaceId);

            if (!workplaceExists) {
                control.controls.workplaceId.setValue(defaultWorkplaceId);
            }
        });
    }

    private getWorkplacesInput(): EmploymentWorkplaceInput[] {
        return this.workplacesControl.getRawValue()
            .filter(workplace => workplace.workplaceId !== null)
            .map(workplace => ({
                workplaceId: workplace.workplaceId!,
                dateFrom: workplace.w_dateFrom,
                dateTo: workplace.w_dateTo,
            }));
    }

    protected readonly close = close;
}
